import { getApiHost } from '../host-by-env';
import { handleError } from 'utilities/utilities';
import { toIsoDateString } from 'utilities/dateFormatter';
import {
  CreateFormModel,
  GetFormDetailsResponse,
  ProductItemPayload,
  SearchFormTemplatesRequest,
  SearchFormTemplatesResponseItem,
  SetProductItemsRequest,
  SetProductItemsResponse,
  UpdateProductItemsRequest,
} from './types';

/**
 * Safely parses a string to a number, returning undefined for empty or invalid strings
 */
function parseNumberOrUndefined(value: string): number | undefined {
  if (!value || value.trim() === '') {
    return undefined;
  }
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? undefined : parsed;
}

function transformFormDataToRequest(formData: CreateFormModel): SetProductItemsRequest {
  const items: ProductItemPayload[] = formData.states.map((state) => ({
    starsItemNumber: formData.formItemNo,
    stateName: state.stateName,
    brandId: state.hasBrandId ? 1 : 0,
    starsItemType: formData.formType,
    starsProductTypeCode: formData.starsProductTypeCode || undefined,
    formDescription: formData.description,
    faccCoverageId: undefined, // Per Paul: un-needed
    faccProductId: parseNumberOrUndefined(state.faccProductId),
    underwriterCode: formData.underwriter,
    formStatus: formData.isActive ? 'Active' : 'Inactive',
    faccExtCoverageId: parseNumberOrUndefined(state.faccExtendedCoverageId),
    faccExtendedProductId: parseNumberOrUndefined(state.faccExtProdId),
    isEJacketEnabled: state.eJacketsEnabled,
    coverageClass: formData.coverageClass,
    isSTARSItemActive: true,
    jacketEffectiveStartDate: toIsoDateString(state.activeDate),
    jacketEffectiveEndDate: toIsoDateString(state.inactiveDate),
    ...(formData.policyTypeDescription !== undefined && { policyTypeDescription: formData.policyTypeDescription }),
  }));

  return { items };
}

/**
 * Creates form product items by posting to the API
 * Each state in the form becomes a separate product item
 * Returns rowCount indicating number of items created
 */
export async function createFormApi(formData: CreateFormModel, token: string): Promise<SetProductItemsResponse> {
  try {
    const url = `${getApiHost()}jacket/manage-form-templates/product-items`;
    const requestPayload = transformFormDataToRequest(formData);

    const response = await fetch(url, {
      method: 'POST',
      body: JSON.stringify(requestPayload),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    const json = await response.json();
    handleError(json);

    // Extract rowCount from result
    const rowCount = json.result?.rowCount || json.rowCount || 0;

    return {
      rowCount,
    };
  } catch (error) {
    console.error('Error creating form:', error);
    throw error;
  }
}

/**
 * Searches form templates by filter criteria
 * Returns a flat list of matching template items
 */
export async function searchFormTemplatesApi(
  request: SearchFormTemplatesRequest,
  token: string,
): Promise<SearchFormTemplatesResponseItem[]> {
  const url = `${getApiHost()}jacket/manage-form-templates/search`;
  const body = { ...request };

  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  const json = await response.json();
  handleError(json);

  return json.result ?? [];
}

/**
 * Deletes a form state (product item) by state code
 */
export async function deleteFormStateApi(formId: string, stateCode: string, token: string): Promise<boolean> {
  try {
    const url = `${getApiHost()}form-management/product-items/${formId}/states/${stateCode}`;

    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const json = await response.json();
    handleError(json);

    return json.result || true;
  } catch (error) {
    console.error('Error deleting form state:', error);
    return false;
  }
}

/**
 * Get form details for editing
 * Returns form-level data and all state records
 */
export async function getFormDetailsApi(
  starsItemNumber: string,
  starsItemType: string,
  underwriterCode: string,
  token: string,
): Promise<GetFormDetailsResponse> {
  const params = new URLSearchParams({
    starsItemNumber,
    starsItemType,
  });

  // Pass empty string for null/undefined underwriter - sproc handles empty string as "match null underwriter"
  params.append('underwriterCode', underwriterCode ?? '');

  const url = `${getApiHost()}jacket/manage-form-templates/details?${params}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  const json = await response.json();
  handleError(json);
  return json.result;
}

/**
 * Update form (full replacement)
 * Transforms form data and sends to update endpoint
 * Deletes removed states, updates existing, inserts new
 */
export async function updateFormApi(
  formData: CreateFormModel,
  originalIdentifier: { starsItemNumber: string; starsItemType: string; underwriterCode: string },
  token: string,
): Promise<SetProductItemsResponse> {
  const url = `${getApiHost()}jacket/manage-form-templates/product-items`;
  const requestPayload: UpdateProductItemsRequest = {
    starsItemNumber: originalIdentifier.starsItemNumber,
    starsItemType: originalIdentifier.starsItemType,
    underwriterCode: originalIdentifier.underwriterCode,
    items: transformFormDataToRequest(formData).items,
  };

  const response = await fetch(url, {
    method: 'PUT',
    body: JSON.stringify(requestPayload),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  const json = await response.json();
  handleError(json);
  return { rowCount: json.result?.rowCount || 0 };
}
